import {
  ContactInfo,
  contactIncludedKey,
  createContactExtraLink,
  firstHiddenOptionalContactLinkField,
  isContactExtraLinkIncluded,
  isContactFieldIncluded,
  isOptionalContactLinkEditorVisible,
  normalizeContactExtraLinks,
  optionalContactLinkEditorKey,
  OPTIONAL_CONTACT_LINK_FIELDS,
  type ContactFieldKey,
  type OptionalContactLinkField,
} from '@applicator/shared'
import type { ReactNode } from 'react'
import { useEffect } from 'react'

import CustomCheckbox from '../CustomCheckbox'
import PhoneField from '../PhoneField'

interface ContactInfoEditorProps {
  data: ContactInfo
  onChange: (field: keyof ContactInfo, value: ContactInfo[keyof ContactInfo]) => void
  onAddExtraFieldRef?: (fn: () => void) => void
}

function ContactFieldRow({
  checked,
  onToggle,
  children,
  fullWidth = false,
}: {
  checked: boolean
  onToggle: (checked: boolean) => void
  children: ReactNode
  fullWidth?: boolean
}) {
  return (
    <div
      className={`contact-info-field-row${fullWidth ? ' contact-info-field-row--full' : ''}`}
    >
      <CustomCheckbox
        checked={checked}
        onChange={onToggle}
        size="xs"
        className="contact-info-field-checkbox"
      />
      <div className="contact-info-field-input min-w-0">{children}</div>
    </div>
  )
}

const inputClassName =
  'w-full min-w-0 px-3 py-2 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring'

function RemoveFieldButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label="Remove field"
      title="Remove field"
      onClick={onClick}
      className="shrink-0 p-1 text-text-muted hover:text-danger opacity-0 group-hover:opacity-100 transition-opacity"
    >
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
        />
      </svg>
    </button>
  )
}

function ContactTextField({
  field,
  data,
  onChange,
  placeholder,
  type = 'text',
  fullWidth = false,
  onRemove,
}: {
  field: ContactFieldKey
  data: ContactInfo
  onChange: (field: keyof ContactInfo, value: ContactInfo[keyof ContactInfo]) => void
  placeholder: string
  type?: 'text' | 'email'
  fullWidth?: boolean
  onRemove?: () => void
}) {
  return (
    <ContactFieldRow
      checked={isContactFieldIncluded(data, field)}
      onToggle={(checked) => onChange(contactIncludedKey(field), checked)}
      fullWidth={fullWidth}
    >
      <div className={`flex items-center gap-1 min-w-0 w-full${onRemove ? ' group' : ''}`}>
        <input
          type={type}
          placeholder={placeholder}
          value={data[field] || ''}
          onChange={(e) => onChange(field, e.target.value)}
          className={inputClassName}
        />
        {onRemove ? <RemoveFieldButton onClick={onRemove} /> : null}
      </div>
    </ContactFieldRow>
  )
}

export default function ContactInfoEditor({ data, onChange, onAddExtraFieldRef }: ContactInfoEditorProps) {
  const extraLinks = normalizeContactExtraLinks(data.additionalLinks)

  const updateExtraLinks = (next: typeof extraLinks) => {
    onChange('additionalLinks', next)
  }

  const removeOptionalLinkField = (field: OptionalContactLinkField) => {
    onChange(field, '')
    onChange(contactIncludedKey(field), false)
    onChange(optionalContactLinkEditorKey(field), false)
  }

  useEffect(() => {
    if (!onAddExtraFieldRef) return
    onAddExtraFieldRef(() => {
      const hiddenField = firstHiddenOptionalContactLinkField(data)
      if (hiddenField) {
        onChange(optionalContactLinkEditorKey(hiddenField), true)
        return
      }
      const current = normalizeContactExtraLinks(data.additionalLinks)
      onChange('additionalLinks', [...current, createContactExtraLink()])
    })
  }, [onAddExtraFieldRef, data, onChange])

  const optionalLinkPlaceholders: Record<OptionalContactLinkField, string> = {
    linkedin: 'LinkedIn',
    substack: 'Substack',
    github: 'GitHub',
  }

  return (
    <div className="contact-info-editor space-y-3">
      <div className="contact-info-editor-grid">
        <ContactTextField field="firstName" data={data} onChange={onChange} placeholder="First Name" />
        <ContactTextField field="middleName" data={data} onChange={onChange} placeholder="Middle Name" />
        <ContactTextField field="lastName" data={data} onChange={onChange} placeholder="Last Name" />
        <ContactTextField
          field="email"
          data={data}
          onChange={onChange}
          placeholder="Email"
          type="email"
        />

        <ContactFieldRow
          checked={isContactFieldIncluded(data, 'phone')}
          onToggle={(checked) => onChange(contactIncludedKey('phone'), checked)}
          fullWidth
        >
          <PhoneField
            value={data.phone || null}
            onChange={(value) => onChange('phone', value || '')}
            className="w-full min-w-0"
          />
        </ContactFieldRow>

        <ContactTextField field="location" data={data} onChange={onChange} placeholder="Location" />
        {OPTIONAL_CONTACT_LINK_FIELDS.map(
          (field) =>
            isOptionalContactLinkEditorVisible(data, field) && (
              <ContactTextField
                key={field}
                field={field}
                data={data}
                onChange={onChange}
                placeholder={optionalLinkPlaceholders[field]}
                fullWidth
                onRemove={() => removeOptionalLinkField(field)}
              />
            ),
        )}

        {extraLinks.map((link) => (
          <div
            key={link.id}
            className="contact-info-field-row contact-info-field-row--full group"
          >
            <CustomCheckbox
              checked={isContactExtraLinkIncluded(link)}
              onChange={(checked) => {
                updateExtraLinks(
                  extraLinks.map((item) =>
                    item.id === link.id ? { ...item, included: checked } : item,
                  ),
                )
              }}
              size="xs"
              className="contact-info-field-checkbox"
            />
            <div className="contact-info-field-input min-w-0 flex items-center gap-1">
              <input
                type="text"
                placeholder="Link or text"
                value={link.url}
                data-contact-extra-id={link.id}
                onChange={(e) => {
                  updateExtraLinks(
                    extraLinks.map((item) =>
                      item.id === link.id ? { ...item, url: e.target.value } : item,
                    ),
                  )
                }}
                className={inputClassName}
              />
              <RemoveFieldButton
                onClick={() => updateExtraLinks(extraLinks.filter((item) => item.id !== link.id))}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
